/** Extract `yyyy-mm-dd` from ISO date/datetime strings (avoids UTC day/year shifts in UI math). */
export function extractIsoYmd(raw: string | null | undefined): string {
  const s = String(raw ?? '').trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
}

/** Parse `yyyy-mm-dd` as a local calendar date. */
export function parseLocalDateFromIsoYmd(isoYmd: string | null | undefined): Date | null {
  const ymd = extractIsoYmd(isoYmd);
  if (!ymd) return null;
  const parts = ymd.split('-').map((n) => parseInt(n, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const [y, mo, d] = parts;
  return new Date(y, mo - 1, d);
}

/** Local `Date` → `yyyy-mm-dd` for form state. */
export function localDateToIsoYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getIsoYmdCalendarYear(raw: string | null | undefined): number | null {
  const ymd = extractIsoYmd(raw);
  if (!ymd) return null;
  const y = parseInt(ymd.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
}

/** Prefer calendar `yyyy-mm-dd`, then fall back to `Date` parsing. */
export function parseWorkflowDate(raw: string | Date | null | undefined): Date | null {
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }
  const local = parseLocalDateFromIsoYmd(raw);
  if (local) return local;
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Locale-aware display; datetime strings are normalized via the `yyyy-mm-dd` prefix. */
export function formatIsoYmdForDisplay(isoYmd: string | null | undefined): string {
  const s = String(isoYmd ?? '').trim();
  if (!s) return '—';
  const ymd = extractIsoYmd(s);
  if (!ymd) return s;
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
